import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import TGButton from "src/components/ui/TGButton";
import { AdjustmentsHorizontalIcon, PlusCircleIcon } from "@heroicons/react/24/outline";
import { TGSelectPickerByList, TGSelectUserPickerByProperties } from "src/components/ui/TGSelect";
import { useState } from "react";
import SortIcon from "src/components/ui-icons/table/SortIcon";
import Loading from "src/components/Loading";
import { TrashIcon } from "@heroicons/react/24/outline";
import { useMemo, useEffect } from "react";
import Pagination from "../components/ui/table/Pagination";
import { usePagination } from "../hooks/usePagination";
import { DrawerComponent, Modal } from "src/components/DialogComponent";
import { FormProvider, Controller } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useRef } from "react";

import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { getItems, getItemsSuccess } from "src/modules/spItems/actions";
import { approversListName, approversDependenciesListName, collectionsListName, typesListName } from "src/api/config/config";
import { useGetSelect } from "src/hooks/redux-selectors";
import Input from "src/components/ui/Input";
import {
  add_row_change_approvers,
  add_row_change_approvers_idle,
  remove_row_change_approvers,
  remove_row_change_approvers_idle,
  update_row_change_approvers,
  update_row_change_approvers_idle,
  get_approvers_documents_by_approvers,
  get_approvers_documents_by_approvers_idle,
  update_approvers_documents_by_approvers,
  update_approvers_documents_by_approvers_idle,
} from "src/modules/changeApprovers/actions";
import { RootState } from "src/store/rootReducer";
import { selectNextPage } from "src/modules/spItems/selectors";
import { nameFromEmail, prefix, removePrefix } from "src/utils/users.utils";
import { autoCloseModal } from "src/constants";

type TableHeadType = "Collection" | "DocumentType" | "Group" | "Approvers" | "NotChange" | "ApprovalRule" | "Index" | "ID";

type Option = {
  id: string;
  name: string;
}

type FormValues = {
  collection: Option[];
  documentType: Option[];
  group: Option[];
  NotChange: Option[];
  approvalRule: Option[];
  index: string;
  approvers: Option[];
};

const allowSelectList = [
  { id: "1", name: "Да" },
  { id: "2", name: "Нет" },
];

const approvalRulesList = [
  { id: "1", name: "Достаточно согласования одного сотрудника" },
  { id: "2", name: "Достаточно согласования всех сотрудников" },
];

const getApproverNumericId = (approver: any): number | undefined => {
  const candidates = [approver?.value?.Id, approver?.value?.id, approver?.Id, approver?.id];
  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null || candidate === "") {
      continue;
    }
    if (typeof candidate === "string" && candidate.includes("@")) {
      continue;
    }
    const id = Number(candidate);
    if (Number.isFinite(id) && id > 0) {
      return id;
    }
  }
  return undefined;
};

const getApproverEmailValue = (approver: any): string => {
  const raw =
    approver?.value?.Email ||
    approver?.value?.EmailAddress ||
    approver?.value?.email ||
    approver?.value?.WorkEmail ||
    approver?.Email ||
    approver?.email ||
    "";
  if (raw) {
    return removePrefix(String(raw), prefix);
  }
  const accountName = approver?.value?.AccountName || approver?.AccountName;
  if (accountName) {
    return removePrefix(String(accountName), prefix);
  }
  if (typeof approver?.id === "string" && approver.id.includes("@")) {
    return removePrefix(approver.id, prefix);
  }
  return "";
};

const getApproverOptionId = (approver: any): string => {
  const email = getApproverEmailValue(approver);
  if (email) {
    return email;
  }
  const numericId = getApproverNumericId(approver);
  if (numericId) {
    return String(numericId);
  }
  return approver?.id != null ? String(approver.id) : "";
};

const getApproverCompareKey = (approver: any): string => {
  const email = getApproverEmailValue(approver).toLowerCase();
  if (email) {
    return `email:${email}`;
  }
  const id = getApproverNumericId(approver);
  if (id) {
    return `id:${String(id)}`;
  }
  return "";
};

const isSameApprover = (left: any, right: any) => {
  const leftKey = getApproverCompareKey(left);
  const rightKey = getApproverCompareKey(right);
  return Boolean(leftKey) && leftKey === rightKey;
};

const uniqueApproversByKey = (approvers: any[] = []) => {
  const seen = new Set<string>();
  return approvers.filter((approver) => {
    const key =
      getApproverCompareKey(approver) ||
      getApproverOptionId(approver) ||
      `keep:${approver?.name || ""}:${getApproverNumericId(approver) || ""}`;
    if (!key || seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const isNullLikeName = (name: any): boolean => {
  if (name === undefined || name === null) {
    return true;
  }
  const normalized = String(name).trim().toLowerCase();
  return (
    !normalized ||
    normalized === "null" ||
    normalized === "undefined" ||
    /^null\b/.test(normalized) ||
    /\(\s*(undefined|null)\s*\)/.test(normalized)
  );
};

const stripApproverNameSuffix = (name: string): string => {
  return name.replace(/\s*\((?:уволен|\d+|нет номера|undefined|null|не активен)\)\s*$/i, "").trim();
};

const isApproverBlocked = (approver: any): boolean => {
  const value = approver?.value || {};
  return value.blocked === true || value.Blocked === true;
};

const getApproverDisplayName = (approver: any): string => {
  const email = getApproverEmailValue(approver);
  const value = approver?.value || {};
  const candidates = [approver?.name, value.Name, value.DisplayName, value.PreferredNameRus];
  const raw = candidates.find((candidate) => candidate && !isNullLikeName(candidate));
  const fio = raw ? stripApproverNameSuffix(String(raw)) : email ? nameFromEmail(email) : "";
  const label = fio || email || "Пользователь";
  if (isApproverBlocked(approver)) {
    return `${label} (уволен)`;
  }
  if (approver?.name && !isNullLikeName(approver.name)) {
    return String(approver.name);
  }
  return label;
};

const mapApproverToPickerOption = (approver: any) => {
  const email = getApproverEmailValue(approver);
  const numericId = getApproverNumericId(approver);
  const value = {
    ...(approver?.value || {}),
    Id: numericId || approver?.value?.Id,
    id: numericId || approver?.value?.id,
    AccountName: approver?.value?.AccountName || (email ? prefix + email : approver?.value?.AccountName),
    Email: email || approver?.value?.email || approver?.value?.Email,
    EmailAddress: email || approver?.value?.email || approver?.value?.Email,
    email: email || approver?.value?.email || approver?.value?.Email,
    blocked: approver?.value?.blocked === true || approver?.value?.Blocked === true,
  };
  return {
    id: getApproverOptionId({ ...approver, value }),
    name: getApproverDisplayName({ ...approver, value }),
    value,
  };
};

const tableHeaderContent = {
  Collection: {
    title: "Коллекция",
    sort: true,
    style: { width: "10%", minWidth: "10%" },
  },
  DocumentType: {
    title: "Тип документа",
    sort: true,
    style: { width: "10%", minWidth: "10%" },
  },
  Group: {
    title: "Группа",
    sort: true,
    style: { width: "15%", minWidth: "15%" },
  },
  Approvers: {
    title: "Согласованты",
    sort: false,
    style: { width: "30%", minWidth: "30%" },
  },
  NotChange: {
    title: "Возможность выбирать",
    sort: true,
    style: { width: "10%", minWidth: "10%" },
  },
  ApprovalRule: {
    title: "Правило согласования",
    sort: true,
    style: { width: "10%", minWidth: "10%" },
  },
  Index: {
    title: "Индекс",
    sort: true,
    style: { width: "10%", minWidth: "10%" },
  },
  Remove: {
    title: "",
    sort: false,
    style: { width: "5%", minWidth: "5%" },
  },
};

console.log('version-change-approvers', 8);

const ChangeApproversForm = ({ type, defaultValues, disabled, setOpenAddRowDrawer, openAddRowDrawer, openRemoveApproverModal, setOpenRemoveApproverModal }: {
  type: "create" | "edit", defaultValues: any, disabled: boolean, setOpenAddRowDrawer: (open: {
    show: boolean;
    type: "create" | "edit";
    item: Record<TableHeadType, any> & { ID: number } | null;
  }) => void; openAddRowDrawer: {
    show: boolean;
    type: "create" | "edit";
    item: Record<TableHeadType, any> & { ID: number } | null;
  }; openRemoveApproverModal: { show: boolean; approver: Option[] | null; approverNew: Option[] | null }; setOpenRemoveApproverModal: (open: { show: boolean; approver: Option[] | null; approverNew: Option[] | null }) => void
}) => {

  const formRef = useRef<HTMLFormElement>(null);
  const pendingRemovedApproverRef = useRef<Option[] | null>(null);
  const currentItemIdRef = useRef<number | null>(openAddRowDrawer.item?.ID ?? null);
  const transferItemIdRef = useRef<number | null>(null);
  const docsCheckItemIdRef = useRef<number | null>(null);
  const dispatch = useAppDispatch();
  const [approverForChange, setApproverForChange] = useState<Option[] | null>([]);
  const [changeApproverError, setChangeApproverError] = useState<string | null>(null);
  const [openChangeApproverSuccessModal, setOpenChangeApproverSuccessModal] = useState(false);

  const MDCollectionsResponseData = useGetSelect(collectionsListName);
  const MDTypesResponseData = useGetSelect(typesListName);
  const MDApproversResponseData = useGetSelect(approversListName);

  const [isDisabledIndex, setIsDisabledIndex] = useState(false);
  const [isDisabledApprovers, setIsDisabledApprovers] = useState(false);

  const methods = useForm<FormValues>({
    defaultValues: defaultValues,
    disabled: disabled,
  });

  const {
    handleSubmit,
    control,
    watch,
    reset,
    getValues,
    setValue,
    formState: { dirtyFields },
  } = methods;

  const watchedIndex = watch('index');
  currentItemIdRef.current = openAddRowDrawer.item?.ID ?? null;

  useEffect(() => {
    if (type == 'edit') {
      if (watchedIndex !== openAddRowDrawer.item?.Index) {
        setIsDisabledApprovers(true);
      } else {
        setIsDisabledApprovers(false);
      }
    }
  }, [watchedIndex]);

  useEffect(() => {
    pendingRemovedApproverRef.current = null;
    transferItemIdRef.current = null;
    docsCheckItemIdRef.current = null;
    setChangeApproverError(null);
    setOpenRemoveApproverModal({ show: false, approver: null, approverNew: null });
    dispatch(get_approvers_documents_by_approvers_idle());
  }, [openAddRowDrawer.show, openAddRowDrawer.item?.ID]);


  const watchedApprovers = watch('approvers');

  useEffect(() => {
    if (type == 'edit') {
      let is_different = false;
      if (
        watchedApprovers && watchedApprovers.length > 0 &&
        openAddRowDrawer.item?.Approvers && openAddRowDrawer.item?.Approvers.length > 0
      ) {
        if (watchedApprovers.length !== openAddRowDrawer.item?.Approvers.length) {
          is_different = true;
        } else {
          const originalKeys = new Set((openAddRowDrawer.item?.Approvers || []).map(getApproverCompareKey));
          watchedApprovers.forEach((approver: any) => {
            if (!originalKeys.has(getApproverCompareKey(approver))) {
              is_different = true;
            }
          });
        }
      }

      if (is_different) {
        setIsDisabledIndex(true);
      } else {
        setIsDisabledIndex(false);
      }
    }
  }, [watchedApprovers]);

  useEffect(() => {

    if (openAddRowDrawer.show && openAddRowDrawer.type == 'edit') {
      setValue("collection", [openAddRowDrawer.item?.Collection]);
      setValue("documentType", [openAddRowDrawer.item?.DocumentType]);
      setValue("group", [openAddRowDrawer.item?.Group]);

      setValue("approvers", uniqueApproversByKey(
        (openAddRowDrawer.item?.Approvers || []).map(mapApproverToPickerOption)
      ));

      setValue("NotChange", [openAddRowDrawer.item?.NotChange == '2' ? { id: '2', name: 'Нет' } : { id: '1', name: 'Да' }]);
      setValue("approvalRule", [{ id: '1', name: 'Все' }]);
      setValue("index", openAddRowDrawer.item?.Index ?? "");
    }

  }, [openAddRowDrawer.show]);

  const handleSubmitForm = (data: FormValues) => {
    if (type == 'edit') {
      if (update_row_change_approvers_status == 'IDLE') {
        dispatch(update_row_change_approvers({
          item: {
            ...openAddRowDrawer.item
          },
          formValues: {
            Index: getValues('index'),
            Approvers: getValues('approvers')
          }
        }));
      }
    } else {
      if (add_row_change_approvers_status == 'IDLE') {
        dispatch(add_row_change_approvers({
          listName: approversDependenciesListName,
          item: {
            Collection: data.collection[0].id,
            DocType: data.documentType[0].id,
            Group: data.group[0].id,
            Approvers: data.approvers.map((approver: any) => ({
              id: approver.id,
              name: approver.name,
              value: approver.value
            })),
            NotChange: data.NotChange[0].id,
            Order0: data.index,
          }
        }));
      }
    }
  }
  const update_approvers_documents_by_approvers_status = useAppSelector((state: RootState) => state.changeApprovers.updateApproversDocumentsByApproversStatus);
  useEffect(() => {
    if (update_approvers_documents_by_approvers_status == 'SUCCESS') {
      dispatch(update_approvers_documents_by_approvers_idle());
      if (transferItemIdRef.current !== currentItemIdRef.current || !openAddRowDrawer.show) {
        return;
      }

      const currentApprovers = getValues('approvers') || [];
      const oldApprovers = openRemoveApproverModal.approver || [];
      const newApprovers = openRemoveApproverModal.approverNew || [];

      const nextApprovers = currentApprovers.filter(
        (item: any) => !oldApprovers.some((oldApprover: any) => isSameApprover(item, oldApprover))
      );
      newApprovers.forEach((approver: any) => {
        if (!nextApprovers.some((item: any) => isSameApprover(item, approver))) {
          nextApprovers.push(approver);
        }
      });

      setValue("approvers", uniqueApproversByKey(nextApprovers));
      setChangeApproverError(null);
      setOpenRemoveApproverModal({ show: false, approver: null, approverNew: null });
      setOpenChangeApproverSuccessModal(true);
    }
    if (update_approvers_documents_by_approvers_status == 'FAILURE') {
      dispatch(update_approvers_documents_by_approvers_idle());
      if (transferItemIdRef.current !== currentItemIdRef.current || !openAddRowDrawer.show) {
        return;
      }
      setChangeApproverError("Не удалось перенести документы. Попробуйте ещё раз.");
    }
  }, [update_approvers_documents_by_approvers_status]);

  const get_approvers_documents_by_approvers_status = useAppSelector((state: RootState) => state.changeApprovers.getApproversDocumentsByApproversStatus);
  const isApproversDocumentsByApprovers = useAppSelector((state: RootState) => state.changeApprovers.isApproversDocumentsByApprovers);
  useEffect(() => {
    if (get_approvers_documents_by_approvers_status == 'SUCCESS') {
      dispatch(get_approvers_documents_by_approvers_idle());
      if (docsCheckItemIdRef.current !== currentItemIdRef.current || !openAddRowDrawer.show) {
        pendingRemovedApproverRef.current = null;
        return;
      }
      if (isApproversDocumentsByApprovers && pendingRemovedApproverRef.current) {
        setChangeApproverError(null);
        setOpenRemoveApproverModal({
          show: true,
          approver: pendingRemovedApproverRef.current,
          approverNew: null,
        });
      } else {
        pendingRemovedApproverRef.current = null;
      }
    }
    if (get_approvers_documents_by_approvers_status == 'FAILURE') {
      dispatch(get_approvers_documents_by_approvers_idle());
      pendingRemovedApproverRef.current = null;
    }
  }, [get_approvers_documents_by_approvers_status]);

  const update_row_change_approvers_status = useAppSelector((state: RootState) => state.changeApprovers.updateStatus);
  useEffect(() => {
    if (update_row_change_approvers_status == 'SUCCESS') {
      dispatch(update_row_change_approvers_idle());
      setOpenAddRowDrawer({
        ...openAddRowDrawer,
        show: false
      })
    }
    if (update_row_change_approvers_status == 'FAILURE') {
      dispatch(update_row_change_approvers_idle());
    }
  }, [update_row_change_approvers_status]);

  const add_row_change_approvers_status = useAppSelector((state: RootState) => state.changeApprovers.addStatus);

  useEffect(() => {
    if (add_row_change_approvers_status == 'SUCCESS') {
      dispatch(add_row_change_approvers_idle());
      setOpenAddRowDrawer({
        ...openAddRowDrawer,
        show: false
      })
    }
    if (add_row_change_approvers_status == 'FAILURE') {
      dispatch(add_row_change_approvers_idle());
    }
  }, [add_row_change_approvers_status]);

  return (
    <FormProvider {...methods}>
      <form
        ref={formRef}
        onSubmit={(e) => {
          const submitter = (e.nativeEvent as SubmitEvent).submitter as HTMLElement | null;
          const active = document.activeElement as HTMLElement | null;
          const fromSearchField =
            active?.getAttribute("type") === "search" ||
            active?.getAttribute("name") === "search" ||
            submitter?.getAttribute("data-save-approvers") !== "true";

          if (fromSearchField) {
            e.preventDefault();
            return;
          }

          handleSubmit(async (data) => {
            handleSubmitForm(data);
          })(e);
        }}
        className="w-full"
      >
        <div className="space-y-5">
          <section className="grid grid-cols-2 grid-rows-2 gap-5 mb-5 custom-selects">
            <div>
              <Controller
                rules={{ required: true }}
                render={({
                  field: { onChange, onBlur, value, name, ref, disabled },
                  fieldState: { invalid, isTouched, isDirty, error },
                  formState,
                }) => (
                  <TGSelectPickerByList
                    list={MDCollectionsResponseData.data.map((d) => ({ id: d.ID.toString(), name: d.Title }))}
                    values={value}
                    onChange={onChange}
                    fetchKey="Collection"
                    classNames={{
                      selectClassName: "!px-5",
                      labelClassName: "text-base font-normal",
                    }}
                    searchPlaceholder="Найти"
                    dataField={{
                      placeholder: "Выберите",
                      type: "peoplePicker",
                      require: false,
                      title: "Коллекция",
                      width: "w-full",
                      key: "peoplePicker",
                      disabled: type == 'edit' && true || false,
                      single: true,
                    }}
                    errors={{}}
                  />
                )}
                name="collection"
                control={control}
              />
            </div>
            <div>
              <Controller
                rules={{ required: true }}
                render={({
                  field: { onChange, onBlur, value, name, ref, disabled },
                  fieldState: { invalid, isTouched, isDirty, error },
                  formState,
                }) => (
                  <TGSelectPickerByList
                    list={MDTypesResponseData.data.map((d) => ({ id: d.ID.toString(), name: d.Title }))}
                    values={value}
                    onChange={onChange}
                    fetchKey="DocumentType"
                    classNames={{
                      selectClassName: "!px-5",
                      labelClassName: "text-base font-normal",
                    }}
                    searchPlaceholder="Найти"
                    dataField={{
                      placeholder: "Выберите",
                      type: "peoplePicker",
                      require: false,
                      title: "Тип документа",
                      width: "w-full",
                      key: "peoplePicker",
                      disabled: type == 'edit' && true || false,
                      single: true,
                    }}
                    errors={{}}
                  />
                )}
                name="documentType"
                control={control}
              />
            </div>
            <div>
              <Controller
                rules={{ required: true }}
                render={({
                  field: { onChange, onBlur, value, name, ref, disabled },
                  fieldState: { invalid, isTouched, isDirty, error },
                  formState,
                }) => (
                  <TGSelectPickerByList
                    list={MDApproversResponseData.data.map((d) => ({ id: d.ID.toString(), name: d.Title }))}
                    values={value}
                    onChange={onChange}
                    fetchKey="Group"
                    classNames={{
                      selectClassName: "!px-5",
                      labelClassName: "text-base font-normal",
                    }}
                    searchPlaceholder="Найти"
                    dataField={{
                      placeholder: "Выберите",
                      type: "peoplePicker",
                      require: false,
                      title: "Группа",
                      width: "w-full",
                      key: "peoplePicker",
                      single: true,
                      disabled: type == 'edit' && true || false,
                    }}
                    errors={{}}
                  />
                )}
                name="group"
                control={control}
              />
            </div>
            <div>
              <Controller
                rules={{ required: true }}
                render={({
                  field: { onChange, onBlur, value, name, ref, disabled },
                  fieldState: { invalid, isTouched, isDirty, error },
                  formState,
                }) => (
                  <TGSelectPickerByList
                    list={allowSelectList}
                    values={value}
                    onChange={onChange}
                    fetchKey="AllowSelect"
                    classNames={{
                      selectClassName: "!px-5",
                      labelClassName: "text-base font-normal",
                    }}
                    searchPlaceholder="Найти"
                    dataField={{
                      placeholder: "Выберите",
                      type: "peoplePicker",
                      require: false,
                      title: "Возможность выбирать",
                      width: "w-full",
                      key: "peoplePicker",
                      single: true,
                      disabled: type == 'edit' && true || false,
                      hideRemoveButton: true
                    }}
                    errors={{}}
                  />
                  // <TGSelectPickerByList
                  //   list={allowSelectList}
                  //   values={value}
                  //   onChange={onChange}
                  //   fetchKey="NotChange"
                  //   classNames={{
                  //     selectClassName: "!px-5",
                  //     labelClassName: "text-base font-normal",
                  //   }}
                  //   searchPlaceholder="Найти"
                  //   dataField={{
                  //     placeholder: "Выберите",
                  //     type: "peoplePicker",
                  //     require: false,
                  //     title: "Возможность выбирать",
                  //     width: "w-full",
                  //     key: "peoplePicker",
                  //     single: true,
                  //   }}
                  //   errors={{}}
                  // />
                )}
                name="NotChange"
                control={control}
              />
            </div>
            <div>
              <Controller
                rules={{ required: true }}
                render={({
                  field: { onChange, onBlur, value, name, ref, disabled },
                  fieldState: { invalid, isTouched, isDirty, error },
                  formState,
                }) => (
                  <TGSelectPickerByList
                    list={approvalRulesList}
                    values={value}
                    onChange={onChange}
                    fetchKey="ApprovalRule"
                    classNames={{
                      selectClassName: "!px-5",
                      labelClassName: "text-base font-normal",
                    }}
                    searchPlaceholder="Найти"
                    dataField={{
                      placeholder: "Выберите",
                      type: "peoplePicker",
                      require: false,
                      title: "Правило согласования",
                      width: "w-full",
                      key: "peoplePicker",
                      single: true,
                      disabled: true,
                      hideRemoveButton: true,
                    }}
                    errors={{}}
                  />
                )}
                name="approvalRule"
                control={control}
              />
            </div>
            <div>
              <label className="flex flex-row justify-between text-gray-900 mb-2.5 text-base font-normal"><span>Индекс</span></label>
              <Controller
                rules={{ required: true }}
                render={({
                  field: { onChange, onBlur, value, name, ref, disabled },
                  fieldState: { invalid, isTouched, isDirty, error },
                  formState,
                }) => (
                  <Input
                    type="text"
                    value={value}
                    onChange={onChange}
                    placeholder="Выберите"
                    width="w-full"
                    key="index"
                    disabled={isDisabledIndex}
                  />
                )}
                name="index"
                control={control}
              />
            </div>
            <div className="col-span-2">
              <Controller
                rules={{ required: true }}
                render={({
                  field: { onChange, onBlur, value, name, ref, disabled },
                  fieldState: { invalid, isTouched, isDirty, error },
                  formState,
                }) => (
                  <TGSelectUserPickerByProperties
                    // showSearch={true}
                    excludeFilters={true}
                    callbackRemoveItem={(e) => {

                      if (type == 'edit') {
                        const removedApprover = mapApproverToPickerOption(e);
                        const originalApprovers = (openAddRowDrawer.item?.Approvers || []).map(mapApproverToPickerOption);
                        const wasSavedApprover = originalApprovers.some((approver: any) => isSameApprover(approver, removedApprover));

                        // Только что выбранный в поиске и ещё не сохранённый
                        // согласовант просто убирается из списка.
                        if (!wasSavedApprover) {
                          return;
                        }

                        pendingRemovedApproverRef.current = [removedApprover];
                        docsCheckItemIdRef.current = openAddRowDrawer.item?.ID ?? null;
                        setChangeApproverError(null);
                        setOpenRemoveApproverModal({
                          show: false,
                          approver: [removedApprover],
                          approverNew: null,
                        });

                        if (get_approvers_documents_by_approvers_status != 'LOADING') {
                          dispatch(get_approvers_documents_by_approvers({
                            approvers: removedApprover
                          }));
                        }
                      }

                    }}
                    values={(value as Option[]) || []}
                    onChange={(v) => onChange(uniqueApproversByKey(v || []))}
                    // values={value}
                    // onChange={onChange}
                    fetchKey="Approvers"
                    classNames={{
                      selectClassName: "!px-5",
                      labelClassName: "text-base font-normal",
                    }}
                    searchPlaceholder="Найти"
                    dataField={{
                      placeholder: "Выберите",
                      type: "peoplePicker",
                      require: false,
                      title: "Согласованты",
                      width: "w-full",
                      key: "peoplePicker",
                      single: false,
                      disabled: isDisabledApprovers,
                    }}
                    errors={{}}
                  />
                  // <TGSelectPickerByList
                  //   list={MDApproversResponseData.data.map((d) => ({ id: d.ID.toString(), name: d.Title }))}
                  //   showSearch={true}
                  //   values={value}
                  //   onChange={onChange}
                  //   fetchKey="Approver"
                  //   classNames={{
                  //     selectClassName: "!px-5",
                  //     labelClassName: "text-base font-normal",
                  //   }}
                  //   searchPlaceholder="Найти"
                  //   dataField={{
                  //     placeholder: "Выберите",
                  //     type: "peoplePicker",
                  //     require: false,
                  //     title: "Согласованты",
                  //     width: "w-full",
                  //     key: "peoplePicker",
                  //     single: false,
                  //   }}
                  //   errors={{}}
                  // />
                )}
                name="approvers"
                control={control}
              />
            </div>
          </section>
          {!disabled && (
            <FlexRow className="justify-between mt-10">
              <>
                <TGButton type="button" onClick={() => setOpenAddRowDrawer({
                  ...openAddRowDrawer,
                  item: null,
                  show: false
                })}>
                  Назад
                </TGButton>
                {
                  (
                    add_row_change_approvers_status == 'LOADING' ||
                    update_row_change_approvers_status == 'LOADING' ||
                    update_approvers_documents_by_approvers_status == 'LOADING' ||
                    get_approvers_documents_by_approvers_status == 'LOADING'
                  ) && (
                    <div className="pt-4">
                      <Loading />
                    </div>
                  ) || (
                    <>
                      {type === "create" ? (
                        <TGButton type="submit" data-save-approvers="true" color="bluedark">
                          Создать
                        </TGButton>
                      ) : (
                        <TGButton type="submit" data-save-approvers="true" color="bluedark">
                          Сохранить изменение
                        </TGButton>
                      )}
                    </>
                  )
                }
              </>
            </FlexRow>
          )}
        </div>
        {/* Отдел */}
      </form>
      {/* Отмена */}
      {/* <CancelModal
        open={openCancelModal}
        toggleCancel={setOpenCancelModal}
        leftButton={() => {
          // setOpenCancelModal(false);
          // cancel();
        }}
        rightButton={() => {
          setOpenCancelModal(false);
        }}
      /> */}
      {/* Удаление согласованта */}
      <Modal
        zIndex={70}
        open={openRemoveApproverModal.show}
        toggleDialog={(state) => {
          if (update_approvers_documents_by_approvers_status == 'LOADING') {
            return;
          }
          setChangeApproverError(null);
          setOpenRemoveApproverModal({ show: false, approver: null, approverNew: null });
          if (openRemoveApproverModal?.approver) {
            setValue("approvers", uniqueApproversByKey([
              ...getValues('approvers'),
              ...openRemoveApproverModal?.approver
            ]));
          }

          // setValue("approvers", openAddRowDrawer.item?.Approvers.map((approver: any) => ({ id: approver.value.id.toString(), name: approver.value.DisplayName, value: {
          //   AccountName: "i:0e.t|microsoftentratrust|" + approver.value.email,
          //   DisplayName: approver.value.DisplayName,
          //   Email: approver.value.email,
          //   Id: approver.value.id
          // } })));
          // setValue("approvers", openAddRowDrawer.item?.Approvers);
          // setApproverForRemove(null);
        }}
        modalClassName="min-h-0 max-w-[600px]"
      >
        <header className="px-10 pb-4">
          <h2>Изменение пользователя</h2>
        </header>

        <div className="px-10 py-5 grow">
          <p>
            На кого перенести документы, ранее отправленные пользователю {openRemoveApproverModal?.approver?.map((approver) => getApproverDisplayName(approver)).join(', ')}?
          </p>
        </div>

        <div className="px-10 py-5">
          <div className="grid grid-cols-1 gap-5 mb-5 custom-selects">
            <div>
              <TGSelectUserPickerByProperties
                excludeFilters={true}
                values={openRemoveApproverModal?.approverNew || []}
                onChange={(values) => {
                  setOpenRemoveApproverModal({
                    ...openRemoveApproverModal,
                    approverNew: values
                  });
                }}
                fetchKey="Approvers"
                classNames={{
                  selectClassName: "!px-5",
                  labelClassName: "text-base font-normal",
                }}
                searchPlaceholder="Найти"
                dataField={{
                  placeholder: "Выберите",
                  type: "peoplePicker",
                  require: true,
                  title: "Согласованты",
                  width: "w-full",
                  key: "peoplePicker",
                  single: true,
                }}
                errors={{}}
              />
            </div>
          </div>
          {changeApproverError && (
            <p className="text-red-600">{changeApproverError}</p>
          )}
        </div>

        <footer className="flex justify-between px-10 pt-10 mt-4 border-t">
          <TGButton type="button" onClick={() => {
            if (update_approvers_documents_by_approvers_status == 'LOADING') {
              return;
            }

            setChangeApproverError(null);
            setOpenRemoveApproverModal({ show: false, approver: null, approverNew: null })
            if (openRemoveApproverModal?.approver) {
              setValue("approvers", uniqueApproversByKey([
                ...getValues('approvers'),
                ...openRemoveApproverModal?.approver
              ]));
            }
          }}>
            Отмена
          </TGButton>
          {update_approvers_documents_by_approvers_status == 'LOADING' && (
            <div className="pt-4">
              <Loading />
            </div>
          ) || (
              <TGButton type="button" color="bluedark" onClick={() => {
                if (openRemoveApproverModal?.approver && openRemoveApproverModal.approverNew && openRemoveApproverModal.approverNew.length > 0) {
                  if (update_approvers_documents_by_approvers_status != 'LOADING') {
                    setChangeApproverError(null);
                    transferItemIdRef.current = openAddRowDrawer.item?.ID ?? null;
                    dispatch(update_approvers_documents_by_approvers({
                      item: openAddRowDrawer.item,
                      approversNew: openRemoveApproverModal.approverNew,
                      approversOld: openRemoveApproverModal.approver
                    }));
                  }
                } else {
                  setChangeApproverError("Выберите сотрудника, на которого нужно перенести документы.");
                }
              }}>
                Изменить
              </TGButton>
            )}
        </footer>
      </Modal>
      {/* Удаление совещания */}
      <Modal
        zIndex={70}
        open={openChangeApproverSuccessModal}
        toggleDialog={() => setOpenChangeApproverSuccessModal(false)}
        modalClassName="min-h-0 max-w-[600px]"
        autoClose={autoCloseModal}
        onAutoClose={() => setOpenChangeApproverSuccessModal(false)}
      >
        <header className="px-10 pb-4">
          <h2>Согласовант изменён</h2>
        </header>

        <div className="px-10 py-5 grow">
          <p>Документы перенесены на выбранного сотрудника. Сохраните форму, чтобы зафиксировать изменение в цепочке согласования.</p>
        </div>

        <footer className="flex justify-center px-10 pt-10 mt-4 border-t">
          <TGButton
            type="button"
            color="bluedark"
            onClick={(e) => {
              e.stopPropagation();
              setOpenChangeApproverSuccessModal(false);
            }}
          >
            Закрыть
          </TGButton>
        </footer>
      </Modal>
    </FormProvider>
  )
}

const ChangeApproversRemoveRow = ({ openRemoveRowModal, setOpenRemoveRowModal, item }: { openRemoveRowModal: { show: boolean; item: any }, setOpenRemoveRowModal: (open: { show: boolean; item: any }) => void, item: Record<TableHeadType, any> & { ID: number } }) => {
  return (
    <>
      <TGButton icon={<TrashIcon width={20} height={20} />} onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        setOpenRemoveRowModal({ show: true, item: item });
      }} color={"white"}>
      </TGButton>
    </>
  )
}

const ChangeApproversAddRow = ({
  openAddRowDrawer,
  setOpenAddRowDrawer,
  openAddRowModal,
  setOpenAddRowModal,
  openRemoveApproverModal,
  setOpenRemoveApproverModal
}: { openAddRowDrawer: { show: boolean; type: "create" | "edit"; item: Record<TableHeadType, any> & { ID: number } | null }, setOpenAddRowDrawer: (open: { show: boolean; type: "create" | "edit"; item: Record<TableHeadType, any> & { ID: number } | null }) => void, openAddRowModal: boolean, setOpenAddRowModal: (open: boolean) => void, openRemoveApproverModal: { show: boolean; approver: Option[] | null; approverNew: Option[] | null }, setOpenRemoveApproverModal: (open: { show: boolean; approver: Option[] | null; approverNew: Option[] | null }) => void }) => {
  return (
    <>
      <TGButton icon={<PlusCircleIcon width={20} height={20} />} onClick={() => setOpenAddRowModal(!openAddRowModal)} color="white">
        Создать строку
      </TGButton>
      <Modal zIndex={70} open={openAddRowModal} toggleDialog={(state) => setOpenAddRowModal(!openAddRowModal)} modalClassName="min-h-0 max-w-[600px]">
        <header className="px-10 pb-4">
          <h2>Создание строки</h2>
        </header>

        <div className="px-10 py-5 grow">
          <p>
            Вы уверены, что хотите создать новую строку? В случае корректировок цепочек согласующих по типам документов, у которых есть документы на согласовании, они будут отклонены системой автоматически.</p>
        </div>

        <footer className="flex justify-between px-10 pt-10 mt-4 border-t">
          <TGButton type="button" onClick={() => setOpenAddRowModal(!openAddRowModal)}>
            Отмена
          </TGButton>
          <TGButton type="button" color="bluedark" onClick={() => {
            setOpenAddRowDrawer({
              type: 'create',
              item: null,
              show: true
            });
            setOpenAddRowModal(!openAddRowModal);
          }}>
            Создать
          </TGButton>
        </footer>
      </Modal>
      <DrawerComponent open={openAddRowDrawer.show} zIndex={50} percentage={"93"} toggleDrawer={() => setOpenAddRowDrawer({ ...openAddRowDrawer, show: false })}>
        <FlexCol className="p-10 shrink custom-form">
          {openAddRowDrawer.type == 'create' ? (
            <h1 className="mb-8 text-black">Создание строки</h1>
          ) : (
            <h1 className="mb-8 text-black">Редактирование строки</h1>
          )}
          <ChangeApproversForm
            type={openAddRowDrawer.type}
            defaultValues={{
              collection: [],
              documentType: [],
              group: [],
              approvers: [],
              NotChange: [{ id: '2', name: 'Нет' }],
              approvalRule: [{ id: '1', name: 'Все' }],
              index: "",
            }}
            disabled={false}
            setOpenAddRowDrawer={setOpenAddRowDrawer}
            openAddRowDrawer={openAddRowDrawer}
            openRemoveApproverModal={openRemoveApproverModal}
            setOpenRemoveApproverModal={setOpenRemoveApproverModal}
          />
        </FlexCol>
      </DrawerComponent>
    </>
  )
}

const ChangeApproversBreadcrumbs = ({ setShowFilters, showFilters, setOpenAddRowDrawer, openAddRowDrawer, openAddRowModal, setOpenAddRowModal, openRemoveApproverModal, setOpenRemoveApproverModal }: { setShowFilters: (show: boolean) => void, showFilters: boolean, setOpenAddRowDrawer: (open: { show: boolean; type: "create" | "edit"; item: Record<TableHeadType, any> & { ID: number } | null }) => void, openAddRowDrawer: { show: boolean; type: "create" | "edit"; item: Record<TableHeadType, any> & { ID: number } | null }, openAddRowModal: boolean, setOpenAddRowModal: (open: boolean) => void, openRemoveApproverModal: { show: boolean; approver: Option[] | null; approverNew: Option[] | null }, setOpenRemoveApproverModal: (open: { show: boolean; approver: Option[] | null; approverNew: Option[] | null }) => void }) => {
  return (
    <FlexRow className="px-7.5 h-[76px] border-b items-center shrink-0">
      <nav className="flex items-center justify-between w-full" aria-label="Breadcrumb">
        <FlexCol className="grow">
          <ol className="inline-flex">
            <li className="inline-flex items-center" aria-current="page">
              <span className="pr-3 text-body-color">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_3437_118829)">
                    <path d="M9.02851 5.5127C7.11601 5.5127 5.54102 7.0877 5.54102 9.0002C5.54102 10.9408 7.11601 12.4877 9.02851 12.4877C10.941 12.4877 12.516 10.9408 12.516 9.0002C12.516 7.0877 10.941 5.5127 9.02851 5.5127ZM9.02851 11.2502C7.79101 11.2502 6.80664 10.2377 6.80664 9.02832C6.80664 7.79082 7.79101 6.80645 9.02851 6.80645C10.266 6.80645 11.2504 7.79082 11.2504 9.0002C11.2504 10.2377 10.2379 11.2502 9.02851 11.2502Z" fill="#637381" />
                    <path d="M17.0724 10.716L15.4974 9.84414C15.5537 9.53477 15.5818 9.22539 15.5818 8.91602C15.5818 8.57852 15.5537 8.26914 15.5255 7.95977L17.1287 7.05977C17.4943 6.86289 17.6349 6.41289 17.4662 6.01914L15.8349 3.26289C15.638 2.92539 15.188 2.78477 14.8224 2.92539L13.1349 3.85352C13.0505 3.79727 12.9662 3.71289 12.9099 3.65664C12.6005 3.40352 12.3193 3.15039 11.8693 2.92539C11.8412 2.92539 11.813 2.89727 11.813 2.84102V1.82852C11.813 1.09727 11.2224 0.478516 10.463 0.478516H7.56616C6.83491 0.478516 6.21616 1.09727 6.21616 1.82852V2.92539C6.21616 2.95352 6.18804 2.98164 6.15991 3.00977C5.73804 3.20664 5.42866 3.48789 5.09116 3.74102C5.03491 3.79727 4.95054 3.85352 4.89429 3.90977L3.40366 3.06602C2.84116 2.75664 2.36304 2.95352 2.16616 3.29102L0.619286 5.96289C0.534911 6.18789 0.506786 6.41289 0.563036 6.63789C0.619286 6.86289 0.759912 7.03164 0.956787 7.14414L2.53179 7.98789C2.47554 8.26914 2.47554 8.57852 2.47554 8.94414C2.47554 9.25352 2.50366 9.56289 2.53179 9.87227L0.900537 10.7723C0.534911 10.9691 0.394286 11.4191 0.563036 11.8129L2.19429 14.5691C2.39116 14.9066 2.81304 15.0473 3.20679 14.9066L4.89429 13.9785C4.97866 14.0348 5.03491 14.091 5.11929 14.1754C5.42866 14.4566 5.73804 14.7098 6.18804 14.9066C6.21616 14.9066 6.24429 14.9348 6.24429 14.991V16.2004C6.24429 16.9316 6.83491 17.5504 7.59429 17.5504H10.463C11.1943 17.5504 11.813 16.9316 11.813 16.2004V14.9066C11.813 14.8785 11.8412 14.8504 11.8693 14.8223C12.2912 14.6254 12.5724 14.3723 12.9099 14.091C12.9943 14.0348 13.0505 13.9785 13.1349 13.8941L14.6255 14.7379C15.188 15.0473 15.638 14.8504 15.863 14.5129L17.438 11.841C17.5505 11.6441 17.5787 11.4191 17.5224 11.2223C17.4662 11.0254 17.2693 10.8285 17.0724 10.716ZM14.9349 13.5004L13.5849 12.741C13.1912 12.516 12.7412 12.5723 12.4037 12.8535C12.263 12.966 12.1505 13.0504 12.038 13.1629C11.7568 13.416 11.5599 13.5848 11.2787 13.6973C10.8287 13.9223 10.5193 14.4004 10.5193 14.9066V16.1441C10.5193 16.2004 10.4912 16.2285 10.4349 16.2285H7.56616C7.50991 16.2285 7.48179 16.2004 7.48179 16.1441V14.9348C7.48179 14.4285 7.17241 13.9504 6.72241 13.7254C6.44116 13.5848 6.21616 13.416 5.93491 13.1629C5.82241 13.0785 5.70991 12.966 5.56929 12.8816C5.37241 12.741 5.14741 12.6566 4.89429 12.6566C4.72554 12.6566 4.52866 12.7129 4.35991 12.7973L3.00991 13.5566L1.88491 11.6441L3.20679 10.9129C3.60054 10.6879 3.82554 10.266 3.76929 9.78789C3.74116 9.50664 3.71304 9.22539 3.71304 8.94414C3.71304 8.63477 3.74116 8.35352 3.76929 8.12852C3.85366 7.65039 3.62866 7.17227 3.20679 6.94727L1.96929 6.24414L3.06616 4.35977L4.41616 5.11914C4.80991 5.34414 5.25991 5.28789 5.59741 5.00664C5.70991 4.92227 5.82241 4.80977 5.93491 4.72539C6.21616 4.47227 6.44116 4.30352 6.72241 4.16289C7.20054 3.93789 7.48179 3.45977 7.48179 2.95352V1.85664C7.48179 1.80039 7.50991 1.77227 7.56616 1.77227H10.4349C10.4912 1.77227 10.5193 1.80039 10.5193 1.85664V2.92539C10.5193 3.43164 10.8287 3.90977 11.2787 4.13477C11.5599 4.27539 11.7568 4.44414 12.038 4.66914C12.1505 4.78164 12.263 4.86602 12.4037 4.97852C12.7412 5.25977 13.2193 5.28789 13.5849 5.06289L14.9349 4.30352L16.0599 6.21602L14.7662 6.94727C14.3724 7.17227 14.1474 7.65039 14.2318 8.12852C14.2599 8.38164 14.288 8.66289 14.288 8.94414C14.288 9.22539 14.2599 9.50664 14.2037 9.78789C14.1193 10.266 14.3443 10.716 14.7662 10.941L16.0318 11.6441L14.9349 13.5004Z" fill="#637381" />
                  </g>
                  <defs>
                    <clipPath id="clip0_3437_118829">
                      <rect width="18" height="18" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </span>
              <div className="inline-flex items-center space-x-2 text-base font-medium cursor-pointer">
                <span>Настройки</span>
              </div>
              <span className="px-3 text-body-color">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M16.2 8.55001L10.3781 2.64376C10.125 2.39064 9.73126 2.39064 9.47813 2.64376C9.22501 2.89689 9.22501 3.29064 9.47813 3.54376L14.2031 8.35314H2.25001C1.91251 8.35314 1.63126 8.63439 1.63126 8.97189C1.63126 9.30939 1.91251 9.61876 2.25001 9.61876H14.2594L9.47813 14.4844C9.22501 14.7375 9.22501 15.1313 9.47813 15.3844C9.59063 15.4969 9.75938 15.5531 9.92813 15.5531C10.0969 15.5531 10.2656 15.4969 10.3781 15.3563L16.2 9.45001C16.4531 9.19689 16.4531 8.80314 16.2 8.55001Z"
                    fill={"#637381"}
                  />
                </svg>
              </span>
            </li>
            <li
              className="flex flex-row items-center justify-between font-medium"
              aria-current="page"
            >
              <span className="px-3 text-body-color">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_3437_118832)">
                    <path d="M17.4371 15.441L16.7621 13.9785C16.6496 13.7254 16.3965 13.5848 16.1152 13.6129L14.484 13.7816C14.1465 13.8098 13.8934 14.1191 13.9215 14.4848C13.9496 14.8223 14.259 15.0754 14.6246 15.0473L14.9621 15.0191C14.484 15.6379 13.7246 16.0035 12.909 16.0035C11.784 16.0035 10.7715 15.3004 10.4059 14.2316C10.2934 13.8941 9.92773 13.7254 9.61836 13.8379C9.28086 13.9504 9.11211 14.316 9.22461 14.6254C9.33711 14.9348 9.47773 15.2441 9.64648 15.5254L9.53399 15.5816C8.69024 15.7785 7.84648 15.8629 7.05898 15.8629C4.86523 15.8629 2.75586 15.0473 1.79961 14.5973C1.91211 12.9098 2.44648 11.4191 3.37461 10.3223C4.35898 9.16914 5.65273 8.55039 7.05898 8.55039C8.43711 8.55039 9.73086 9.16914 10.6871 10.2941C10.3496 10.5754 10.0402 10.9129 9.81524 11.3066L9.67461 10.9691C9.53399 10.6598 9.16836 10.491 8.83086 10.6316C8.52148 10.7723 8.35273 11.1379 8.49336 11.4754L9.14023 12.966C9.25273 13.191 9.47773 13.3598 9.73086 13.3598C9.75898 13.3598 9.75898 13.3598 9.78711 13.3598L11.4184 13.2191C11.7559 13.191 12.0371 12.8816 11.9809 12.5441C11.9246 12.2066 11.6434 11.9535 11.3059 11.9816L10.884 12.0098C11.334 11.2223 12.2059 10.716 13.1621 10.716C14.3715 10.716 15.4121 11.5316 15.7215 12.6848C15.8059 13.0223 16.1434 13.2191 16.4809 13.1348C16.8184 13.0504 17.0152 12.7129 16.9309 12.3754C16.4809 10.6598 14.934 9.47852 13.134 9.47852C12.6559 9.47852 12.1777 9.56289 11.7559 9.73164C11.7277 9.67539 11.6996 9.64726 11.6434 9.59101C10.8277 8.63476 9.84336 7.95977 8.74648 7.62227C9.84336 7.00352 10.5746 5.82227 10.5746 4.50039C10.5746 2.53164 8.97148 0.900391 6.97461 0.900391C4.97773 0.900391 3.45898 2.36289 3.45898 4.35977C3.45898 5.70977 4.19023 6.86289 5.28711 7.48164C4.19023 7.81914 3.17773 8.49414 2.39023 9.45039C1.20898 10.8566 0.533984 12.7973 0.505859 14.9348C0.505859 15.1879 0.646484 15.4129 0.843359 15.5254C1.57461 15.891 4.21836 17.1004 7.03086 17.1004C7.93086 17.1004 8.88711 16.9879 9.81524 16.791C9.84336 16.791 9.87148 16.791 9.89961 16.7629L10.5746 16.5098C11.2215 16.9879 12.0371 17.241 12.8809 17.241C14.2027 17.241 15.384 16.5941 16.0871 15.5535L16.284 15.9473C16.3965 16.1723 16.6215 16.3129 16.8465 16.3129C16.9309 16.3129 17.0152 16.2848 17.0996 16.2566C17.4652 16.116 17.6059 15.7504 17.4371 15.441ZM7.03086 2.02539C8.32461 2.02539 9.36523 3.06602 9.36523 4.35977C9.36523 5.65352 8.32461 6.69414 7.03086 6.69414C5.73711 6.69414 4.69648 5.65352 4.69648 4.35977C4.69648 3.06602 5.76523 2.02539 7.03086 2.02539Z" fill="#637381" />
                  </g>
                  <defs>
                    <clipPath id="clip0_3437_118832">
                      <rect width="18" height="18" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </span>
              <div className="inline-flex items-center space-x-2 text-base font-medium">
                Изменение согласованта
              </div>
            </li>
          </ol>
        </FlexCol>
        <FlexCol>
          <FlexRow className="gap-x-2.5">
            <ChangeApproversAddRow
              openAddRowDrawer={openAddRowDrawer}
              setOpenAddRowDrawer={setOpenAddRowDrawer}
              openAddRowModal={openAddRowModal}
              setOpenAddRowModal={setOpenAddRowModal}
              openRemoveApproverModal={openRemoveApproverModal}
              setOpenRemoveApproverModal={setOpenRemoveApproverModal}
            />
            <TGButton icon={<AdjustmentsHorizontalIcon width={20} height={20} />} onClick={() => { setShowFilters(!showFilters) }} color={showFilters ? "bluedark" : "white"}>
            </TGButton>
          </FlexRow>
        </FlexCol>
      </nav>
    </FlexRow>
  )
}

const ChangeApproversFilters = ({
  filters,
  setFilters,
  setPage,
  collectionOptions,
  documentTypeOptions,
  groupOptions,
}: {
  filters: any,
  setFilters: (filters: any) => void,
  setPage: (page: number) => void,
  collectionOptions: Option[],
  documentTypeOptions: Option[],
  groupOptions: Option[],
}) => {

  return (
    <div className="grid grid-cols-4 gap-5 mb-5 custom-selects px-7.5 py-4">
      <div>
        <TGSelectPickerByList
          list={collectionOptions}
          values={filters.Collection}
          onChange={(values) => {
            setFilters({ ...filters, Collection: values })
            setPage(1)
          }}
          fetchKey="Collection"
          classNames={{
            selectClassName: "!px-5",
            labelClassName: "text-base font-normal",
          }}
          searchPlaceholder="Найти"
          dataField={{
            placeholder: "Выберите",
            type: "peoplePicker",
            require: false,
            title: "Коллекция",
            width: "w-full",
            key: "peoplePicker",
          }}
          errors={{}}
        />
      </div>
      <div>
        <TGSelectPickerByList
          list={documentTypeOptions}
          values={filters.DocumentType}
          onChange={(values) => {
            setFilters({ ...filters, DocumentType: values })
            setPage(1)
          }}
          fetchKey="DocumentType"
          classNames={{
            selectClassName: "!px-5",
            labelClassName: "text-base font-normal",
          }}
          searchPlaceholder="Найти"
          dataField={{
            placeholder: "Выберите",
            type: "peoplePicker",
            require: false,
            title: "Тип документа",
            width: "w-full",
            key: "peoplePicker",
          }}
          errors={{}}
        />
      </div>
      <div>
        <TGSelectPickerByList
          list={groupOptions}
          values={filters.Group}
          onChange={(values) => {
            setFilters({ ...filters, Group: values })
            setPage(1)
          }}
          fetchKey="Group"
          classNames={{
            selectClassName: "!px-5",
            labelClassName: "text-base font-normal",
          }}
          searchPlaceholder="Найти"
          dataField={{
            placeholder: "Выберите",
            type: "peoplePicker",
            require: false,
            title: "Группа",
            width: "w-full",
            key: "peoplePicker",
          }}
          errors={{}}
        />
      </div>
      <div>
        <TGSelectPickerByList
          list={allowSelectList}
          values={filters.AllowSelect}
          onChange={(values) => {
            setFilters({ ...filters, AllowSelect: values })
            setPage(1)
          }}
          fetchKey="AllowSelect"
          classNames={{
            selectClassName: "!px-5",
            labelClassName: "text-base font-normal",
          }}
          searchPlaceholder="Найти"
          dataField={{
            placeholder: "Выберите",
            type: "peoplePicker",
            require: false,
            title: "Возможность выбирать",
            width: "w-full",
            key: "peoplePicker",
            single: true
          }}
          errors={{}}
        />
      </div>
      <div>
        <TGSelectPickerByList
          list={approvalRulesList}
          values={[
            { id: '1', name: 'Достаточно согласования одного сотрудника' },
          ]}
          onChange={(values) => {
            // setFilters({ ...filters, ApprovalRule: values })
            // setPage(1)
          }}
          fetchKey="ApprovalRule"
          classNames={{
            selectClassName: "!px-5",
            labelClassName: "text-base font-normal",
          }}
          searchPlaceholder="Найти"
          dataField={{
            placeholder: "Выберите",
            type: "peoplePicker",
            require: false,
            title: "Правило согласования",
            width: "w-full",
            key: "peoplePicker",
            single: true,
          }}
          errors={{}}
        />
      </div>
      <div>
        <div className="w-full mb-5 ">
          <label className="flex flex-row justify-between text-gray-900 mb-2.5 text-base font-normal"><span>Индекс</span></label>
          <div>
            <Input
              placeholder="Выберите"
              onChange={(e: any) => {
                setFilters({ ...filters, Order0: e.target.value })
                setPage(1)
              }}
              value={filters.Order0}
            />
          </div>
        </div>
      </div>
      <div className="col-span-2">
        <TGSelectUserPickerByProperties
          excludeFilters={true}
          values={filters.Users || []}
          onChange={(values) => {
            setFilters({ ...filters, Users: values })
            setPage(1)
          }}
          fetchKey="Approvers"
          classNames={{
            selectClassName: "!px-5",
            labelClassName: "text-base font-normal",
          }}
          searchPlaceholder="Найти"
          dataField={{
            placeholder: "Выберите",
            type: "peoplePicker",
            require: false,
            title: "Согласованты",
            width: "w-full",
            key: "peoplePicker",
            single: false,
          }}
          errors={{}}
        />
      </div>
    </div>
  )
}

const TableHeaderColumn = ({ tableHeaderContent, sortDirection, onClick }: { tableHeaderContent: any, sortDirection: any, onClick: any }) => {
  return (
    <FlexCol className="select-none px-4" style={tableHeaderContent.style}>
      <FlexRow className="items-center gap-x-2">
        {tableHeaderContent.icon && <FlexCol>{tableHeaderContent.icon}</FlexCol>}
        <FlexCol className="font-medium text-black whitespace-pre-line w-fit">{tableHeaderContent.title}</FlexCol>
        {tableHeaderContent.sort && (
          <FlexCol className="w-auto cursor-pointer" onClick={onClick}>
            <SortIcon sortDirection={sortDirection} />
          </FlexCol>
        )}
      </FlexRow>
    </FlexCol>
  );
}

const ChangeApproversTable = ({
  list,
  loading,
  openRemoveRowModal,
  setOpenRemoveRowModal,
  openAddRowDrawer,
  setOpenAddRowDrawer,
  limit,
  nextPage,
  page,
  setPage,
  sortedField,
  setSortedField,
  sortDirection,
  setSortDirection
}: {
  list: (Record<TableHeadType, any> & { ID: number })[];
  loading: boolean;
  openRemoveRowModal: { show: boolean; item: any };
  setOpenRemoveRowModal: (open: { show: boolean; item: any }) => void;
  openAddRowDrawer: {
    show: boolean;
    type: "create" | "edit";
    item: Record<TableHeadType, any> & { ID: number } | null;
  };
  setOpenAddRowDrawer: (open: {
    show: boolean;
    type: "create" | "edit";
    item: Record<TableHeadType, any> & { ID: number } | null;
  }) => void;
  limit: number;
  nextPage: any;
  page: any;
  setPage: any;
  sortedField: keyof typeof tableHeaderContent | null;
  setSortedField: (field: keyof typeof tableHeaderContent | null) => void;
  sortDirection: any;
  setSortDirection: any;
}) => {

  const dispatch = useAppDispatch();
  const remove_row_change_approvers_status = useAppSelector((state: RootState) => state.changeApprovers.removeStatus);


  const handleSort = (field: keyof typeof tableHeaderContent) => {
    if (sortedField === field) {
      setSortDirection((prevDirection: any) => prevDirection === "asc" ? "desc" : prevDirection === "desc" ? "default" : "asc");
    } else {
      setSortedField(field);
      setSortDirection("asc");
    }
  };

  const sortedTableBodyContent = useMemo(() => {

    const sortedData = [...list];

    if (!sortedField || sortDirection === "default") {

      // сортировка по умолчанию: Коллекция -> Тип документа -> Индекс
      const [locales, options]: [Intl.LocalesArgument, Intl.CollatorOptions] = [["ru", "en"], { sensitivity: "base" }];

      sortedData.sort((a, b) => {
        const collectionCompare = a.Collection.name.localeCompare(b.Collection.name, locales, options);
        if (collectionCompare !== 0) return collectionCompare;

        const documentTypeCompare = a.DocumentType.name.localeCompare(b.DocumentType.name, locales, options);
        if (documentTypeCompare !== 0) return documentTypeCompare;

        const indexA = Number(a.Index);
        const indexB = Number(b.Index);

        if (!Number.isNaN(indexA) && !Number.isNaN(indexB)) {
          return indexA - indexB;
        }

        return String(a.Index).localeCompare(String(b.Index), locales, options);
      });

    } else {

      const [locales, options]: [Intl.LocalesArgument, Intl.CollatorOptions] = [["ru", "en"], { sensitivity: "base" }];

      sortedData.sort((a, b) => {
        const valueA = a[sortedField as keyof (Record<TableHeadType, any> & { ID: number })];
        const valueB = b[sortedField as keyof (Record<TableHeadType, any> & { ID: number })];

        if (typeof valueA === "string" && typeof valueB === "string") {
          return sortDirection === "asc"
            ? valueA.localeCompare(valueB, locales, options)
            : valueB.localeCompare(valueA, locales, options);
        } else if (typeof valueA === "boolean" && typeof valueB === "boolean") {
          return sortDirection === "asc" ? Number(valueA) - Number(valueB) : Number(valueB) - Number(valueA);
        } else if (typeof valueA === 'object' && typeof valueB === 'object') {
          return sortDirection === "asc"
            ? valueA.name.localeCompare(valueB.name, locales, options)
            : valueB.name.localeCompare(valueA.name, locales, options);
        }

        return 0;
      });
    }

    return sortedData;
  }, [sortedField, sortDirection, list.length, page]) as (Record<TableHeadType, any> & { ID: number })[];

  // const {
  //   numberOfPages,
  //   pageItems,
  //   length: activeMeetingsItemsCount,
  // } = usePagination(sortedTableBodyContent, limit, page);

  // function onChangePage(page: number) {
  //   setPage(page);
  // }

  // useEffect(() => {
  //   onChangePage(1);
  // }, [list]);

  useEffect(() => {
    if (remove_row_change_approvers_status == 'SUCCESS') {
      dispatch(remove_row_change_approvers_idle());
      setOpenRemoveRowModal({ show: false, item: null });
    }
  }, [remove_row_change_approvers_status]);

  return (
    <div className="px-7.5 py-4">
      <FlexRow>
        <FlexCol className="w-full">
          <FlexRow className="px-6 py-5 overflow-x-auto bg-white rounded-lg table-custom shadow-custom scrollbar scrollbar-thumb-rounded-lg scrollbar-h-2 scrollbar-thumb-[#D1D5DB]">
            <FlexCol className="divide-y min-w-[1235px] grow">
              <FlexRow className="pb-5">
                {Object.keys(tableHeaderContent).map((fieldKey) => {
                  const key = fieldKey as keyof typeof tableHeaderContent;

                  return (
                    <TableHeaderColumn
                      key={fieldKey}
                      tableHeaderContent={tableHeaderContent[key]}
                      sortDirection={sortedField === key ? sortDirection : "default"}
                      onClick={() => handleSort(key)}
                    />
                  );
                })}
              </FlexRow>
              {(
                Array.isArray(sortedTableBodyContent) && sortedTableBodyContent.length > 0 ? (
                  sortedTableBodyContent.map((item: Record<TableHeadType, any> & { ID: number }, index: number) => (
                    <>
                      {index < page * 10 && (
                        <FlexRow
                          className="py-4  hover:bg-[#F3F4F6] hover:bg-opacity-50 rounded-lg  cursor-pointer -mx-2 px-2"
                          key={item.ID}
                          onClick={() => {
                            setOpenAddRowDrawer({
                              show: true,
                              type: "edit",
                              item: item,
                            });
                          }}
                        >
                          <FlexCol className="px-4 break-words text-darkblue " style={tableHeaderContent.Collection.style}>
                            {item.Collection.name}
                          </FlexCol>
                          <FlexCol className="px-4 break-words text-[#637381]" style={tableHeaderContent.DocumentType.style}>
                            {item.DocumentType.name}
                          </FlexCol>
                          <FlexCol className="px-4 break-words text-[#637381]" style={tableHeaderContent.Group.style}>
                            {item.Group.name}
                          </FlexCol>
                          <FlexCol className="px-4 break-words text-[#637381]" style={tableHeaderContent.Approvers.style}>
                            <div dangerouslySetInnerHTML={{ __html: item.Approvers.map((approver: any) => getApproverDisplayName(approver)).join(", ") }} />
                          </FlexCol>
                          <FlexCol className="px-4 break-words text-[#637381]" style={tableHeaderContent.NotChange.style}>
                            {item.NotChange == '1' ? 'Да' : 'Нет'}
                          </FlexCol>
                          <FlexCol className="px-4 break-words text-[#637381]" style={tableHeaderContent.ApprovalRule.style}>
                            {item.ApprovalRule}
                          </FlexCol>
                          <FlexCol className="px-4 break-words text-[#637381]" style={tableHeaderContent.Index.style}>
                            {item.Index}
                          </FlexCol>
                          <FlexCol className="px-4 break-words text-[#637381]" style={tableHeaderContent.Remove.style}>
                            <ChangeApproversRemoveRow openRemoveRowModal={openRemoveRowModal} setOpenRemoveRowModal={setOpenRemoveRowModal} item={item} />
                          </FlexCol>
                        </FlexRow>
                      )}
                    </>
                  ))
                ) : (
                  <div>Нет данных</div>
                )
              )}
            </FlexCol>
          </FlexRow>
        </FlexCol>
      </FlexRow>

      {loading ? (
        <div className="pt-4">
          <Loading />
        </div>
      ) : (
        <>
          {page * 10 < sortedTableBodyContent.length && (
            <FlexRow>
              <FlexCol className="w-full">
                <TGButton onClick={() => setPage((prev: any) => prev + 1)} color="bluedark">
                  Подгрузить еще
                </TGButton>
              </FlexCol>
            </FlexRow>
          )}
        </>
      )}
      {/* {activeMeetingsItemsCount > limit && (
        <Pagination numberOfPages={numberOfPages} page={page} onChange={onChangePage} />
      )}                       */}
      <Modal zIndex={70} open={openRemoveRowModal.show} toggleDialog={(state) => setOpenRemoveRowModal({ ...openRemoveRowModal, show: false })} modalClassName="min-h-0 max-w-[600px]">
        <header className="px-10 pb-4">
          <h2>Подтвердите действие</h2>
        </header>

        <div className="px-10 py-5 grow">
          <p>Вы уверены, что хотите удалить строку? В случае корректировок цепочек согласующих по типам документов, у которых есть документы на согласовании, они будут отклонены системой автоматически.</p>
        </div>

        <footer className="flex justify-between px-10 pt-10 mt-4 border-t">
          <TGButton type="button" onClick={() => setOpenRemoveRowModal({ show: false, item: null })}>
            Отмена
          </TGButton>
          {remove_row_change_approvers_status == 'LOADING' && (
            <Loading />
          ) || (
              <TGButton type="button" color="bluedark" onClick={() => {
                if (remove_row_change_approvers_status == 'IDLE') {
                  dispatch(remove_row_change_approvers({ item: openRemoveRowModal.item }));
                }
              }}>
                Удалить
              </TGButton>
            )}
        </footer>
      </Modal>
    </div>
  )
}

const ChangeApprovers = () => {

  const limit = 1000;

  const dispatch = useAppDispatch();
  const [showFilters, setShowFilters] = useState(false);
  const [openAddRowDrawer, setOpenAddRowDrawer] = useState<{
    show: boolean;
    type: "create" | "edit";
    item: (Record<TableHeadType, any> & { ID: number }) | null;
  }>({
    show: false,
    type: "create",
    item: null,
  });
  const [openRemoveApproverModal, setOpenRemoveApproverModal] = useState<{
    show: boolean;
    approver: Option[] | null;
    approverNew: Option[] | null;
  }>({
    show: false,
    approver: [],
    approverNew: [],
  });
  const [sortedField, setSortedField] = useState<keyof typeof tableHeaderContent | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | "default">("default");
  const delete_row_change_approvers_status = useAppSelector((state: RootState) => state.changeApprovers.removeStatus);
  const update_row_change_approvers_status = useAppSelector((state: RootState) => state.changeApprovers.updateStatus);
  const add_row_change_approvers_status = useAppSelector((state: RootState) => state.changeApprovers.addStatus);
  const [openAddRowModal, setOpenAddRowModal] = useState(false);
  const [openRemoveRowModal, setOpenRemoveRowModal] = useState<{
    show: boolean;
    item: any;
  }>({
    show: false,
    item: null,
  });
  const [filters, setFilters] = useState<any>({
    Order0: null,
    Collection: [],
    Users: [],
    DocumentType: [],
    Group: [],
    AllowSelect: [],
  });

  const [page, setPage] = useState(1);
  // const nextPage = useAppSelector((state) => selectNextPage(state, approversDependenciesListName));
  const nextPage = null;

  useEffect(() => {
    dispatch(getItems({ listName: collectionsListName }));
    dispatch(getItems({ listName: typesListName }));
    dispatch(getItems({ listName: approversListName }));
    dispatch(getItems({
      listName: approversDependenciesListName,
      rowLimit: limit,
      sortOrder: [
        {
          name: "Collection.Title",
          asc: true,
        },
        {
          name: "DocType.Title",
          asc: true,
        },
      ],
    }));
  }, []);

  useEffect(() => {
    if (
      delete_row_change_approvers_status === "SUCCESS" ||
      update_row_change_approvers_status === "SUCCESS" ||
      add_row_change_approvers_status === "SUCCESS"
    ) {
      dispatch(getItems({
        listName: approversDependenciesListName,
        rowLimit: limit,
        sortOrder: [
          {
            name: "Collection.Title",
            asc: true,
          },
          {
            name: "DocType.Title",
            asc: true,
          },
        ],
      }));
    }
  }, [
    add_row_change_approvers_status,
    delete_row_change_approvers_status,
    dispatch,
    limit,
    update_row_change_approvers_status,
  ]);

  // useEffect(() => {

  //   if(page == 1){
  //     return;
  //   }

  //   let change_filters: any = {};

  //   if(filters?.AllowSelect?.length){
  //     if(filters.AllowSelect[0].id == '1'){
  //       change_filters.NotChange = false;
  //     } else {
  //       change_filters.NotChange = true;
  //     }
  //   }

  //   if(filters?.Users?.length) {
  //     if(filters.AllowSelect?.length && filters.AllowSelect[0].id == '1'){
  //       change_filters.DefaultApprovers = filters?.Users?.map((approver: any) => approver.value.Id)
  //     } else {
  //       change_filters.Users = filters?.Users?.map((approver: any) => approver.value.Id)
  //     }
  //   }

  //   if(filters?.Collection?.length) {
  //     change_filters.Collection = filters?.Collection?.map((collection: any) => collection.id)
  //   }

  //   if(filters?.DocumentType?.length) {
  //     change_filters.DocType = filters?.DocumentType?.map((documentType: any) => documentType.id)
  //   }

  //   if(filters?.Group?.length) {
  //     change_filters.Group = filters?.Group?.map((group: any) => group.id)
  //   }

  //   if(filters?.Order0) {
  //     change_filters.Order0 = filters?.Order0
  //   }

  //   let data: any = { 
  //     listName: approversDependenciesListName,
  //     rowLimit: limit,
  //     nextPage: nextPage,
  //     merge: true,
  //     filters: {
  //       ...change_filters
  //     }
  //   };
  //   // if(sortDirection === "default"){
  //     // data['sortOrder'] = { name: "Collection.Title", asc: true };
  //     data['sortOrder'] = [
  //       { 
  //         name: "Collection.Title", 
  //         asc: true 
  //       },
  //       { 
  //         name: "DocType.Title", 
  //         asc: true 
  //       },
  //     ];
  //   // }

  //   dispatch(getItems(data));

  // },[
  //   page
  // ])

  const { data, status } = useGetSelect(approversDependenciesListName);
  const sourceList = useMemo(() => {
    if (status !== "SUCCESS" || !Array.isArray(data)) {
      return [];
    }

    return data.map((d) => {
      let Approvers = [];
      const sourceUsers = d.NotChange
        ? (Array.isArray(d.DefaultApprovers) ? d.DefaultApprovers : [])
        : (Array.isArray(d.Users) ? d.Users : []);

      Approvers = sourceUsers.filter(Boolean).map((user: any) => {
        const metadata = user.Metadata && typeof user.Metadata === "object"
          ? user.Metadata
          : (user.value && typeof user.value === "object" ? user.value : user);
        return {
          id: user.ID ?? user.id ?? metadata?.id ?? metadata?.Id,
          name: user.Name ?? user.name ?? metadata?.Name ?? metadata?.DisplayName,
          Email: user.Email ?? user.email ?? metadata?.email ?? metadata?.Email,
          value: metadata,
        };
      });

      if (
        Approvers && Approvers.length > 0
      ) {
        Approvers = Approvers.map((approver: any) => {

          let values = {};

          if (approver.value && approver.value.UserProfileProperties) {
            let UserProfilePropertiesValues = {};

            approver.value.UserProfileProperties.forEach((property: any) => {
              UserProfilePropertiesValues = {
                ...UserProfilePropertiesValues,
                [property.Key]: property.Value
              }
            });
            values = { ...approver.value, ...UserProfilePropertiesValues };
          } else {
            values = { ...(approver.value || {}) };
          }

          const email = getApproverEmailValue({ ...approver, value: values });
          const mapped = {
            ...approver,
            id: approver.id ?? (values as any)?.id ?? (values as any)?.Id,
            Email: email || approver.Email,
            value: {
              ...values,
              Email: email || (values as any)?.Email || (values as any)?.email,
              email: email || (values as any)?.email || (values as any)?.Email,
              AccountName: (values as any)?.AccountName || (email ? prefix + email : (values as any)?.AccountName),
              blocked: (values as any)?.blocked === true || (values as any)?.Blocked === true,
            }
          };

          return {
            ...mapped,
            name: getApproverDisplayName(mapped),
          };
        });
      }

      return {
        ID: d.ID,
        Collection: { id: d.Collection?.ID.toString(), name: d.Collection?.Title },
        DocumentType: { id: d.DocType?.ID.toString(), name: d.DocType?.Title },
        Group: { id: d.Group?.ID.toString(), name: d.Group?.Title },
        Approvers: Approvers,
        Users: d.Users,
        DefaultApprovers: d.DefaultApprovers,
        NotChange: d.NotChange ? '2' : '1',
        ApprovalRule: 'Достаточно согласования одного сотрудника',
        Index: d.Order0.toString(),
      };
    });
  }, [data, status]);

  const applyFilters = (rows: any[], currentFilters: any) => {
    const selectedCollectionIds = (currentFilters?.Collection || []).map((item: Option) => item.id);
    const selectedDocumentTypeIds = (currentFilters?.DocumentType || []).map((item: Option) => item.id);
    const selectedGroupIds = (currentFilters?.Group || []).map((item: Option) => item.id);
    const selectedAllowSelect = (currentFilters?.AllowSelect || []).map((item: Option) => item.id);
    const selectedUserIds = (currentFilters?.Users || [])
      .map((item: any) => item?.value?.Id || item?.id)
      .filter(Boolean)
      .map((id: string | number) => id.toString());

    return rows.filter((row) => {
      if (selectedCollectionIds.length > 0 && !selectedCollectionIds.includes(row?.Collection?.id)) {
        return false;
      }

      if (selectedDocumentTypeIds.length > 0 && !selectedDocumentTypeIds.includes(row?.DocumentType?.id)) {
        return false;
      }

      if (selectedGroupIds.length > 0 && !selectedGroupIds.includes(row?.Group?.id)) {
        return false;
      }

      if (selectedAllowSelect.length > 0 && !selectedAllowSelect.includes(row?.NotChange)) {
        return false;
      }

      if (selectedUserIds.length > 0) {
        const selectedAllowSelectId = selectedAllowSelect[0];
        const usersForFilter = selectedAllowSelectId === "1"
          ? (Array.isArray(row?.DefaultApprovers) ? row.DefaultApprovers : [])
          : (Array.isArray(row?.Users) ? row.Users : []);
        const rowUserIds = usersForFilter
          .map((user: any) => user?.ID)
          .filter(Boolean)
          .map((id: string | number) => id.toString());

        const hasMatchedUser = selectedUserIds.some((id: string) => rowUserIds.includes(id));

        if (!hasMatchedUser) {
          return false;
        }
      }

      if (currentFilters?.Order0 && row?.Index !== currentFilters.Order0.toString()) {
        return false;
      }

      return true;
    });
  };

  const filteredList = useMemo(() => applyFilters(sourceList, filters), [filters, sourceList]);

  const getUniqueOptionsByField = (rows: any[], field: "Collection" | "DocumentType" | "Group") => {
    const unique = new Map<string, Option>();

    rows.forEach((row) => {
      const fieldValue = row?.[field];
      const optionId = fieldValue?.id;
      const optionName = fieldValue?.name;

      if (!optionId || !optionName || unique.has(optionId)) {
        return;
      }

      unique.set(optionId, { id: optionId, name: optionName });
    });

    return Array.from(unique.values());
  };

  const collectionOptions = useMemo(() => {
    const rowsForOptions = applyFilters(sourceList, { ...filters, Collection: [] });
    return getUniqueOptionsByField(rowsForOptions, "Collection");
  }, [filters, sourceList]);

  const documentTypeOptions = useMemo(() => {
    const rowsForOptions = applyFilters(sourceList, { ...filters, DocumentType: [] });
    return getUniqueOptionsByField(rowsForOptions, "DocumentType");
  }, [filters, sourceList]);

  const groupOptions = useMemo(() => {
    const rowsForOptions = applyFilters(sourceList, { ...filters, Group: [] });
    return getUniqueOptionsByField(rowsForOptions, "Group");
  }, [filters, sourceList]);

  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, [filters?.AllowSelect, filters?.Collection, filters?.DocumentType, filters?.Group, filters?.Order0, filters?.Users]);

  return (
    <div>
      <ChangeApproversBreadcrumbs
        setShowFilters={setShowFilters}
        showFilters={showFilters}
        openAddRowDrawer={openAddRowDrawer}
        setOpenAddRowDrawer={setOpenAddRowDrawer}
        openAddRowModal={openAddRowModal}
        setOpenAddRowModal={setOpenAddRowModal}
        openRemoveApproverModal={openRemoveApproverModal}
        setOpenRemoveApproverModal={setOpenRemoveApproverModal}
      />
      {showFilters && <ChangeApproversFilters
        filters={filters}
        setFilters={setFilters}
        setPage={setPage}
        collectionOptions={collectionOptions}
        documentTypeOptions={documentTypeOptions}
        groupOptions={groupOptions}
      />}
      <ChangeApproversTable
        loading={status === "LOADING"}
        list={filteredList}
        openRemoveRowModal={openRemoveRowModal}
        setOpenRemoveRowModal={setOpenRemoveRowModal}
        openAddRowDrawer={openAddRowDrawer}
        setOpenAddRowDrawer={setOpenAddRowDrawer}
        limit={limit}
        nextPage={nextPage}
        page={page}
        setPage={setPage}
        sortedField={sortedField}
        setSortedField={setSortedField}
        sortDirection={sortDirection}
        setSortDirection={setSortDirection}
      />
    </div>
  )
}

export default ChangeApprovers;